import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { PAGE_DEFAULT } from "../constants/common";
import { IOption } from "../types/common";

const PAGE_SIZES = ["10", "25", "50", "100"];

// Keeps the page in ?pageIndex=&pageSize= so a refresh, a shared link or Back restores it.
// Invalid values fall back to page 1 and the default size.
export const useUrlPaging = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageIndex = Number(searchParams.get("pageIndex"));
  const currentPage = Number.isInteger(pageIndex) && pageIndex >= 1 ? pageIndex : 1;
  const sizeParam = searchParams.get("pageSize") ?? "";
  const pageSize = PAGE_SIZES.includes(sizeParam) ? sizeParam : String(PAGE_DEFAULT.pageSize);
  const limit = useMemo<IOption>(() => ({ label: pageSize, id: pageSize }), [pageSize]);

  // One replace per user action: separate calls in the same tick would overwrite each other.
  const updateQuery = useCallback(
    (changes: Record<string, string>) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(changes).forEach(([key, value]) => next.set(key, value));
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  const setCurrentPage = useCallback(
    (page: number) => updateQuery({ pageIndex: String(page) }),
    [updateQuery]
  );

  // Also resets to page 1, so PaginationNew's separate setCurrentPage(1) is harmless.
  const setLimit = useCallback(
    (option: IOption) => updateQuery({ pageIndex: "1", pageSize: String(option.id) }),
    [updateQuery]
  );

  return { searchParams, updateQuery, currentPage, setCurrentPage, limit, setLimit };
};
